"use client";

import { useEffect, useState } from "react";
import {
  Clock,
  Users,
  ShoppingCart,
  DollarSign,
  RefreshCw,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { useDashboardPanel } from "@/hooks/useDashboardPanel";
import { Button } from "@/components/ui/button";

const defaultStats = [
  {
    icon: <Clock size={20} className="text-purple-500" />,
    value: "-",
    label: "Total sales",
  },
  {
    icon: <Users size={20} className="text-cyan-500" />,
    value: "-",
    label: "Customers",
  },
  {
    icon: <Users size={20} className="text-orange-500" />,
    value: "-",
    label: "Return Customers",
  },
  {
    icon: <ShoppingCart size={20} className="text-red-500" />,
    value: "-",
    label: "Products",
  },
  {
    icon: <DollarSign size={20} className="text-green-500" />,
    value: "-",
    label: "Total Orders",
  },
];

const Statistic = () => {
  const [stats, setStats] = useState(defaultStats);

  const dashboardStats = useSelector(
    (state: RootState) => state.dashboard?.stats,
  );
  const dashboardSnapshot = useSelector(
    (state: RootState) => state.dashboard?.snapshot,
  );
  const { refreshPanel, isRefreshing } = useDashboardPanel("statistics");

  useEffect(() => {
    if (!dashboardStats) return;

    const formatNumber = (num: number) => {
      if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
      if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
      return num.toString();
    };

    const mapped = [
      {
        ...defaultStats[0],
        value: dashboardStats.total_sales
          ? `₹${formatNumber(dashboardStats.total_sales)}`
          : "-",
      },
      {
        ...defaultStats[1],
        value: dashboardStats.customer_count ?? "-",
      },
      {
        ...defaultStats[2],
        value: dashboardStats.returning_customer_rate
          ? `${dashboardStats.returning_customer_rate}%`
          : "-",
      },
      { ...defaultStats[3], value: dashboardStats.product_count ?? "-" },
      { ...defaultStats[4], value: dashboardStats.total_orders ?? "-" },
    ];
    setStats(mapped);
  }, [dashboardStats]);

  return (
    <Card className="w-full border-border/60 bg-card/85 backdrop-blur-xl shadow-theme-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle>Statistics</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={refreshPanel}
            disabled={isRefreshing}
            className="h-8 w-8 p-0"
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {stats.map((stat, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 rounded-xl border border-border/40 bg-background/60 p-3 shadow-sm backdrop-blur-sm"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
                {stat.icon}
              </div>
              <div>
                <p className="text-base font-semibold tracking-tight text-foreground">
                  {stat.value}
                </p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
        {dashboardSnapshot?.sales_by_state ||
        dashboardSnapshot?.sales_by_countries ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-border/40 bg-background/60 p-3 shadow-sm backdrop-blur-sm">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Top state
              </p>
              <p className="mt-1 text-sm font-semibold text-foreground">
                {dashboardSnapshot?.sales_by_state?.top_state || "N/A"}
              </p>
              <p className="text-xs text-muted-foreground">
                {dashboardSnapshot?.sales_by_state?.top_state_sales
                  ? `₹${Number(dashboardSnapshot.sales_by_state.top_state_sales).toLocaleString("en-IN")}`
                  : "No sales summary"}
              </p>
            </div>
            <div className="rounded-xl border border-border/40 bg-background/60 p-3 shadow-sm backdrop-blur-sm">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Deliveries
              </p>
              <p className="mt-1 text-sm font-semibold text-foreground">
                {dashboardSnapshot?.sales_by_countries?.active_deliveries ?? 0}
              </p>
              <p className="text-xs text-muted-foreground">
                {dashboardSnapshot?.sales_by_countries?.recent_deliveries ?? 0}{" "}
                recent deliveries
              </p>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default Statistic;
